import React from "react";
import { createApi } from "../../../utils/createApi";
import { AxiosError, AxiosResponse } from "axios";
import { universalAPIErrorHandler } from "../../../utils/universalAPIErrorHandler";
import { NavigateFunction } from "react-router-dom";
import { Dispatcher } from "../../Sites/SiteAboutPage/SiteAboutPageUtils/siteAboutInterfaces";
import type {
  LineCrossing,
  LineDirection,
  BackendLineDirection,
} from "../../EditCamera/editCameraStore/editCameraStore";
import { LINE_COLORS } from "../../EditLineCrossings/EditLineCrossingsUtils/lineColors";
import { buildLineCrossingsListPath } from "../../EditLineCrossings/EditLineCrossingsUtils/lineCrossingsListPath";
import { normalizeBackendDirection } from "../../EditLineCrossings/EditLineCrossingsUtils/lineDirection";

const isSideAOnLeft = (
  coordinates: [[number, number], [number, number]]
): boolean => {
  const [point1, point2] = coordinates;
  const dx = point2[0] - point1[0];
  const dy = point2[1] - point1[1];
  if (dy !== 0) return dy < 0;
  return dx < 0;
};

const backendToFrontendDirection = (
  rawBackendDirection: string,
  coordinates: [[number, number], [number, number]]
): LineDirection => {
  // ENG-1317: case-insensitive; see normalizeBackendDirection.
  const backendDirection = normalizeBackendDirection(rawBackendDirection);
  if (backendDirection === "both") return "Both";
  const sideAIsLeft = isSideAOnLeft(coordinates);
  if (backendDirection === "l2r") {
    return sideAIsLeft ? "AtoB" : "BtoA";
  }
  return sideAIsLeft ? "BtoA" : "AtoB";
};

const isLegacyDirectionFormat = (direction: string): boolean =>
  direction === "AtoB" || direction === "BtoA" || direction === "Both";

export const getLineCrossingsDialogInfo = (
  row: any,
  setSelectedAlertCameraInfo: any,
  setLineCrossingsSelectedAlert: (lines: LineCrossing[]) => void,
  setLoadingLineCrossingsInfo: Dispatcher<boolean>,
  navigate: NavigateFunction,
  onComplete?: () => void
) => {
  const AxiosUI = createApi(``);

  const retryFunction = () => {
    getLineCrossingsDialogInfo(
      row,
      setSelectedAlertCameraInfo,
      setLineCrossingsSelectedAlert,
      setLoadingLineCrossingsInfo,
      navigate,
      onComplete
    );
  };

  const handleComplete = () => {
    setLoadingLineCrossingsInfo(false);
    onComplete?.();
  };

  AxiosUI.get(`/camera/${row.camera_pk}/status/`)
    .then((statusResponse: AxiosResponse) => {
      setSelectedAlertCameraInfo(statusResponse.data);
      return Promise.all([
        // Default (camera-wide) lines only — preset-scoped lines stay out of the alert dialog.
        AxiosUI.get(buildLineCrossingsListPath(row.camera_pk)),
        AxiosUI.get(`/metric/?camera_id=${row.camera_pk}`),
      ]);
    })
    .then(([lineCrossingsResponse, metricsResponse]) => {
      const allowedLabels = [
        "intruder_line_crossing",
        "vehicle_line_crossing",
        "bike_line_crossing",
      ];
      const lineCrossings = lineCrossingsResponse.data.results || [];
      const processedLines: LineCrossing[] = lineCrossings.map(
        (line: any, index: number) => {
          let frontendDirection: LineDirection;
          if (isLegacyDirectionFormat(line.direction || "")) {
            frontendDirection = (line.direction as LineDirection) || "Both";
          } else {
            frontendDirection = backendToFrontendDirection(
              (line.direction as BackendLineDirection) || "both",
              line.coordinates
            );
          }
          return {
            id: line.id,
            camera: line.camera,
            metric_labels: line.metric_labels || [],
            coordinates: line.coordinates,
            direction: frontendDirection,
            color: LINE_COLORS[index % LINE_COLORS.length],
          };
        }
      );
      setLineCrossingsSelectedAlert(processedLines);
      handleComplete();
    })
    .catch((reason: AxiosError) => {
      universalAPIErrorHandler(reason, navigate, retryFunction, () => {
        setLineCrossingsSelectedAlert([]);
        handleComplete();
      });
    });
};
